import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Search, User, Menu, Sun, Moon, BookOpen, Users, Trophy, Sparkles, Home, Grid, FolderHeart, CreditCard, LayoutDashboard, Settings, LogOut, Shield, PenTool, BarChart3, Wallet, Flame } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const location = useLocation();

  const isCreator = user?.subscription_tier === 'creator' || user?.subscription_tier === 'contributor';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const navLinks = [
    { label: 'Browse', path: '/browse' },
    { label: 'Creators', path: '/creators' },
    { label: 'Pricing', path: '/pricing' },
  ];

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[var(--header-bg)] backdrop-blur-md border-b border-[var(--border)]' : 'bg-transparent'
        }`}
        data-testid="header"
      >
        <div className="page-padding py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1.5" data-testid="header-logo">
            <Flame size={24} className="text-[#FF2424]" />
            <span className="font-teko text-2xl font-bold tracking-wider bg-gradient-to-r from-[#FF2424] to-[#FF4444] bg-clip-text text-transparent">
              SUPREMICS
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.path 
                    ? 'text-[var(--primary)]' 
                    : isScrolled 
                      ? 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                      : 'text-white/80 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-1">
            <button 
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isScrolled ? 'text-[var(--text-secondary)] hover:bg-[var(--surface)]' : 'text-white/70 hover:text-white'}`}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <button className={`p-2 rounded-lg transition-colors ${isScrolled ? 'text-[var(--text-secondary)] hover:bg-[var(--surface)]' : 'text-white/70 hover:text-white'}`}>
              <Search size={18} />
            </button>

            {user && (
              <Link to="/dashboard" className={`p-2 rounded-lg transition-colors ${isScrolled ? 'text-[var(--text-secondary)] hover:bg-[var(--surface)]' : 'text-white/70 hover:text-white'}`}>
                <User size={18} />
              </Link>
            )}

            <button 
              onClick={() => setIsMenuOpen(true)}
              className={`p-2 rounded-lg transition-colors ${isScrolled ? 'text-[var(--text-secondary)] hover:bg-[var(--surface)]' : 'text-white/70 hover:text-white'}`}
              data-testid="header-menu-btn"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Slide-in Menu */}
      <div 
        className={`fixed inset-0 z-[100] transition-opacity duration-300 ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setIsMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div 
          className={`absolute right-0 top-0 h-full w-full max-w-md bg-[var(--background)] shadow-2xl transition-transform duration-300 ease-out overflow-y-auto ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Menu Header */}
          <div className="sticky top-0 bg-[var(--background)] border-b border-[var(--border)] p-4 flex items-center justify-between z-10">
            <span className="font-teko text-xl font-bold text-[var(--primary)]">Menu</span>
            <button 
              onClick={() => setIsMenuOpen(false)}
              className="p-2 hover:bg-[var(--surface)] rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-4 space-y-6">
            {/* User Section */}
            {user ? (
              <div className="p-4 bg-[var(--surface)] rounded-xl border border-[var(--border)]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--primary)]/20 flex items-center justify-center">
                    <span className="font-teko text-lg text-[var(--primary)]">{user.username?.[0]?.toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">{user.username}</p>
                    <p className="text-xs text-[var(--text-secondary)] capitalize">{user.subscription_tier || 'Free'} Plan</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link 
                    to="/dashboard" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex-1 py-2 text-center text-xs bg-[var(--background)] rounded-lg hover:bg-[var(--primary)]/10 transition-colors"
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/profile" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex-1 py-2 text-center text-xs bg-[var(--background)] rounded-lg hover:bg-[var(--primary)]/10 transition-colors"
                  >
                    Settings
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                <Link 
                  to="/login" 
                  onClick={() => setIsMenuOpen(false)}
                  className="flex-1 py-3 text-center text-sm border border-[var(--border)] rounded-xl hover:border-[var(--primary)] transition-colors"
                >
                  Sign In
                </Link>
                <Link 
                  to="/signup" 
                  onClick={() => setIsMenuOpen(false)}
                  className="flex-1 py-3 text-center text-sm bg-[var(--primary)] text-white rounded-xl hover:bg-[var(--primary)]/90 transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            )}

            {/* Explore Section */}
            <div>
              <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-3">Explore</p>
              <div className="grid grid-cols-2 gap-2">
                <MenuLink to="/" icon={Home} label="Home" onClick={() => setIsMenuOpen(false)} />
                <MenuLink to="/browse" icon={BookOpen} label="Browse Comics" onClick={() => setIsMenuOpen(false)} />
                <MenuLink to="/creators" icon={Users} label="Creators" onClick={() => setIsMenuOpen(false)} />
                <MenuLink to="/collections" icon={FolderHeart} label="Collections" onClick={() => setIsMenuOpen(false)} />
                <MenuLink to="/leaderboard" icon={Trophy} label="Leaderboard" onClick={() => setIsMenuOpen(false)} />
                <MenuLink to="/pricing" icon={CreditCard} label="Pricing" onClick={() => setIsMenuOpen(false)} />
              </div>
            </div>

            {/* Creator Studio Section - Only for creators */}
            {isCreator && (
              <div>
                <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-3">Creator Studio</p>
                <div className="space-y-1">
                  <MenuLinkFull to="/creator-studio" icon={Sparkles} label="Comic Creator Studio" desc="Upload, AI enhance & publish - all in one!" highlight onClick={() => setIsMenuOpen(false)} />
                  <MenuLinkFull to="/create" icon={PenTool} label="Quick Upload" desc="Simple comic upload" onClick={() => setIsMenuOpen(false)} />
                  <MenuLinkFull to="/ai-tools" icon={Sparkles} label="AI Tools Only" desc="Colorize, translate & enhance" onClick={() => setIsMenuOpen(false)} />
                  <MenuLinkFull to="/analytics" icon={BarChart3} label="Analytics" desc="Track your performance" onClick={() => setIsMenuOpen(false)} />
                  <MenuLinkFull to="/creator/earnings" icon={Wallet} label="Earnings" desc="Withdraw your revenue" onClick={() => setIsMenuOpen(false)} />
                </div>
              </div>
            )}

            {/* Admin Section */}
            {isAdmin && (
              <div>
                <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-3">Admin</p>
                <MenuLinkFull to="/admin" icon={Shield} label="Admin Panel" desc="Manage site & users" onClick={() => setIsMenuOpen(false)} />
              </div>
            )}

            {/* Quick Links */}
            <div>
              <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-3">Help & Info</p>
              <div className="flex flex-wrap gap-2">
                <QuickLink to="/about" label="About" onClick={() => setIsMenuOpen(false)} />
                <QuickLink to="/customer-service" label="Support" onClick={() => setIsMenuOpen(false)} />
                <QuickLink to="/privacy" label="Privacy" onClick={() => setIsMenuOpen(false)} />
                <QuickLink to="/terms" label="Terms" onClick={() => setIsMenuOpen(false)} />
              </div>
            </div>

            {/* Logout */}
            {user && (
              <button
                onClick={() => { logout(); setIsMenuOpen(false); }}
                className="w-full py-3 flex items-center justify-center gap-2 text-sm text-red-500 hover:bg-red-500/10 rounded-xl transition-colors"
              >
                <LogOut size={16} />
                Sign Out
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Menu Link Component - Grid style
function MenuLink({ to, icon: Icon, label, onClick }) {
  return (
    <Link 
      to={to} 
      onClick={onClick}
      className="flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--surface)] transition-colors group"
    >
      <Icon size={18} className="text-[var(--text-secondary)] group-hover:text-[var(--primary)]" />
      <span className="text-sm">{label}</span>
    </Link>
  );
}

// Menu Link Full Width - with description
function MenuLinkFull({ to, icon: Icon, label, desc, highlight, onClick }) {
  return (
    <Link 
      to={to} 
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
        highlight 
          ? 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 hover:border-purple-500/40' 
          : 'hover:bg-[var(--surface)]'
      }`}
    >
      <div className={`p-2 rounded-lg ${highlight ? 'bg-purple-500/20' : 'bg-[var(--surface)]'}`}>
        <Icon size={18} className={highlight ? 'text-purple-400' : 'text-[var(--text-secondary)]'} />
      </div>
      <div>
        <p className={`text-sm font-medium ${highlight ? 'text-purple-400' : ''}`}>{label}</p>
        <p className="text-xs text-[var(--text-secondary)]">{desc}</p>
      </div>
    </Link>
  );
}

// Quick Link - Small pill style
function QuickLink({ to, label, onClick }) {
  return (
    <Link 
      to={to} 
      onClick={onClick}
      className="px-3 py-1.5 text-xs text-[var(--text-secondary)] bg-[var(--surface)] rounded-full hover:text-[var(--primary)] transition-colors"
    >
      {label}
    </Link>
  );
}

export default Header;
